import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { TailSpin } from "react-loader-spinner";
import "./dashboardV2.css";

const PerTeamOtpsCountGraph = ({ apiEndpoint, title }) => {
  const today = new Date();
  const initialEndDate = new Date(today.setHours(23, 59, 59, 999));
  const initialStartDate = new Date(today.setDate(today.getDate()));

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");

  const fetchData = async () => {
    setLoading(true);
    const response = await axios.post(apiEndpoint, {
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    });
    setChartData(formatDataForChart(response.data, title));
    setLoading(false);
  };

  const formatDataForChart = (data) => {
    return Object.keys(data)
      .filter(
        (key) =>
          data[key].team_name !== "dummy team" &&
          data[key].team_name !== "Ghana Team" &&
          data[key].team_name !== "Team 5"
      )
      .map((key) => ({
        id: data[key].team_name,
        date_range_otps_count: data[key].date_range_otps_count,
        date_range_otps: data[key].date_range_otps,
        dup_otps_date_range: data[key].dup_otps_date_range,
      }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="dashboard-v2-tooltip">
        <div className="dashboard-v2-tooltip-label">{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} className="dashboard-v2-tooltip-row" style={{ color: p.color }}>
            {p.name}: {p.value?.toLocaleString()}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="dashboard-v2-chart-card">
      <div className="dashboard-v2-chart-header">
        <h2 className="dashboard-v2-chart-title">{title}</h2>
        <div className="dashboard-v2-chart-controls">
          <select
            className="dashboard-v2-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="line">Line Chart</option>
            <option value="bar">Bar Chart</option>
          </select>
        </div>
      </div>
      <div className="dashboard-v2-date-row">
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          className="dashboard-v2-datepicker"
          dateFormat="MM/dd/yyyy"
        />
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          className="dashboard-v2-datepicker"
          dateFormat="MM/dd/yyyy"
        />
        <button className="dashboard-v2-submit-btn" onClick={fetchData}>
          Apply
        </button>
      </div>
      {loading ? (
        <div className="dashboard-v2-loading">
          <TailSpin height={48} width={48} color="#38bdf8" ariaLabel="loading" />
        </div>
      ) : (
        chartData.length > 0 && (
          <div className="dashboard-v2-chart-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              {chartType === "line" ? (
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="id" stroke="#64748b" fontSize={10} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#38bdf8", strokeWidth: 1, strokeDasharray: "4 4" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="date_range_otps_count" stroke="#38bdf8" strokeWidth={2} name="Valid OTP Count" dot={{ fill: "#38bdf8" }} />
                  <Line type="monotone" dataKey="date_range_otps" stroke="#10b981" strokeWidth={2} name="Total OTP Generated" dot={{ fill: "#10b981" }} />
                  <Line type="monotone" dataKey="dup_otps_date_range" stroke="#f59e0b" strokeWidth={2} name="Duplicate OTPs" dot={{ fill: "#f59e0b" }} />
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barRadius={[6, 6, 0, 0]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="id" stroke="#64748b" fontSize={10} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(56, 189, 248, 0.08)" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="date_range_otps_count" fill="#38bdf8" name="Valid OTP Count" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="date_range_otps" fill="#10b981" name="Total OTP Generated" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="dup_otps_date_range" fill="#f59e0b" name="Duplicate OTPs" radius={[6, 6, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )
      )}
    </div>
  );
};

export default PerTeamOtpsCountGraph;
